import Vue, { CreateElement } from '@src/vue'
import { Component, Prop, Watch } from 'vue-property-decorator'

import { Subtitle1 } from '@src/elements/typography/typography'
import { BloodPressureForm } from '@src/routes/members/components/body-measurements/form'
import { HistoryTable } from '@src/routes/members/components/body-measurements/table'
import {
  CHART_LABEL_TWO_ROWS_FONT_STYLE,
  lineColorMapping,
  MIN_LANDSCAPE_WIDTH
} from '@src/util/constants'
import { MetricData } from '@src/data/body-measurement'
import { BodyMeasurementsCard } from '@src/routes/members/components/body-measurements/body-measurements-card'
import { Button } from '@src/elements/button/button'
import { ErrorScreen } from '@src/components/error-screen/error-screen'
import { convertToLocalDate } from '@src/util/date-format'
import { displayDateForChart } from '@src/util/functions'

import './blood-pressure.sass'
import { getChartRef } from '@src/components/chart/utils'

enum BloodPressureIntervals {
  OPTIMAL = 0,
  NORMAL = 1,
  PREHYPERTENSION = 2,
  HIGH1 = 3,
  HIGH2 = 4,
  HYPERTENSIVE_CASE = 5
}

@Component
export class BloodPressure extends Vue {

  @Prop({ required: true })
  measurements: {
    systolicBloodPressure: MetricData[],
    diastolicBloodPressure: MetricData[]
  }

  @Prop({ required: true })
  protected bloodPressureNotes: string

  private isCardExpanded: boolean = false
  private portraitMatch: any
  private initialChartWidth: any = '100%'
  private initialChartHeight: number = 300
  private series: any[]
  private chartOptions: any

  constructor () {
    super()
    this.series = this.getSeries()
    this.chartOptions = this.getChartOptions()
  }

  mounted () {
    this.portraitMatch = window.matchMedia('(orientation: portrait)')
    this.addEventListeners()
  }

  @Watch('measurements', { deep: true, immediate: true })
  setSeriesOnMeasurementsSystolicBloodLoad () {
    this.series = this.getSeries()
    this.chartOptions = this.getChartOptions()
  }

  beforeDestroy () {
    this.removeEventListeners()
  }

  expandCard () {
    this.isCardExpanded = !this.isCardExpanded
  }

  beforePrint () {
    const chart = getChartRef(this.$refs, 'bloodChartRef')

    const printWidth = 650
    const printHeight = 300
    this.initialChartWidth = this.initialChartWidth || chart.opts.chart.width
    this.initialChartHeight = this.initialChartHeight || chart.opts.chart.height
    chart.updateOptions({
      chart: {
        width: printWidth,
        height: printHeight,
        animations: {
          enabled: false
        }
      }
    })
  }

  afterPrint () {
    const chart = getChartRef(this.$refs, 'bloodChartRef')

    chart.updateOptions({
      chart: {
        width: this.initialChartWidth,
        height: this.initialChartHeight,
        animations: {
          enabled: false
        }
      }
    })
  }

  getChartOptions () {
    return {
      chart: {
        type: 'line',
        height: 300,
        redrawOnParentResize: true,
        redrawOnWindowResize: true,
        zoom: {
          enabled: false
        },
        toolbar: {
          show: false
        },
        animations: {
          enabled: false
        }
      },
      grid: {
        padding: {
          right: 50
        }
      },
      markers: {
        discrete: this.series ? this.series[0].data.map((point, index) => ({
          seriesIndex: 0,
          dataPointIndex: index,
          fillColor: point.marker.fillColor,
          strokeColor: '#fff',
          size: 7,
          shape: 'circle'
        })) : []
      },
      stroke: {
        width: 1.5,
        curve: 'smooth'
      },
      legend: {
        show: false
      },
      tooltip: {
        onDatasetHover: {
          highlightDataSeries: true
        },
        shared: false,
        intersect: true,
        x: {
          show: true,
          formatter: function (value) {
            return convertToLocalDate(value)
          }
        }
      },
      yaxis: {
        forceNiceScale: true,
        min: 0,
        max: 5,
        stepSize: 1,
        tickAmount: 5,
        labels: {
          style: this.getYAxisLabelStyle(),
          maxWidth: 500,
          formatter: (val) => {
            return this.$t(`BODY_MEASUREMENTS.BLOOD_PRESSURE_CARD.${Object.values(BloodPressureIntervals)[val]}`)
          }
        }
      },
      xaxis: {
        type: 'datetime',
        labels: {
          tickAmount: 'dataPoints',
          style: this.getXAxisLabelStyle(),
          formatter: (val) => {
            return displayDateForChart({ value: val }, 'month')
          }
        },
        tooltip: {
          enabled: false
        }
      }
    }
  }

  getSeries () {
    const measurements = this.measurements.systolicBloodPressure
      .map(systolic => {
        const diastolic = this.measurements.diastolicBloodPressure.filter(diastolic => diastolic.effectiveAt === systolic.effectiveAt)[0]
        if (diastolic) {
          const category = this.getBloodPressureCategory(systolic.value, diastolic.value)
          return {
            x: new Date(systolic.effectiveAt).getTime(),
            y: BloodPressureIntervals[category],
            tooltip: `${systolic.value}/${diastolic.value}`,
            marker: { fillColor: lineColorMapping[category] }
          }
        }
      })
      // a systolic value without a diastolic value of the same time is not a data point
      .filter(point => point)
      .sort((a, b) => a!.x > b!.x ? 1 : a!.x < b!.x ? -1 : 0)

    return [{ data: measurements, name: this.$t('BODY_MEASUREMENTS.BLOOD_PRESSURE') }]
  }

  render (h: CreateElement) {
    if (!this.chartOptions) return ''

    return <div class='measurements-section blood-pressure-wrapper'>
      <Subtitle1 class='title'>{this.$t('BODY_MEASUREMENTS.BLOOD_PRESSURE')}</Subtitle1>

      <div class='device-measurement-section-wrapper'>
        {this.measurements.systolicBloodPressure.length === 0 || this.measurements.diastolicBloodPressure.length === 0 ?
          <ErrorScreen icon='brand/empty-graph' message={this.$t('BODY_MEASUREMENTS.EMPTY_GRAPH.BLOOD_PRESSURE')} />
          : <apexchart ref='bloodChartRef' height={this.initialChartHeight} options={this.chartOptions} series={this.series} /> }

        <div class='cards-header-row row'>
          <Button type='remove' click={this.expandCard} icon={this.isCardExpanded ? 'util/caret-up' : 'util/caret-down'}
                  label={this.$t(this.isCardExpanded ? 'COLLAPSE' : 'EXPAND')} ref='expandBtn' class='expand-btn' />
        </div>

        {this.isCardExpanded ? this.renderCard(h) : null}
      </div>
    </div>
  }

  renderCard (h: CreateElement) {
    return <BodyMeasurementsCard type='bloodPressure'>
      <div slot='left'>
        <BloodPressureForm notes={{ bloodPressureNotes: this.bloodPressureNotes }} />
      </div>
      <div slot='right'>
        <HistoryTable history={this.getTableHistory()} measuringUnit={this.$t('UNITS.MMHG')} title={this.$t('BODY_MEASUREMENTS.BLOOD_PRESSURE_CARD.KNOWN_BLOOD_PRESSURE')}
                      translationKey='BODY_MEASUREMENTS.BLOOD_PRESSURE_CARD.' type='bloodPressure' />
      </div>
    </BodyMeasurementsCard>
  }

  protected getTableHistory () {
    return this.convertToHistoryDTO(this.measurements.systolicBloodPressure, this.measurements.diastolicBloodPressure)
      .sort((a, b) => a.effectiveAt > b.effectiveAt ? -1 : a.effectiveAt < b.effectiveAt ? 1 : 0)
  }

  protected convertToHistoryDTO (systolicMeasurements: MetricData[], diastolicMeasurements: MetricData[]) {
    if (!systolicMeasurements || !diastolicMeasurements) {
      return []
    }

    return systolicMeasurements
      .filter(systolic => diastolicMeasurements.some(diastolic => diastolic.effectiveAt === systolic.effectiveAt))
      .map(systolic => {
        const diastolic = diastolicMeasurements.filter(diastolic => diastolic.effectiveAt === systolic.effectiveAt)[0]
        return {
          id: {
            systolic: systolic.id,
            diastolic: diastolic.id
          },
          effectiveAt: systolic.effectiveAt,
          value: `${systolic.value}/${diastolic.value}`,
          type: this.getBloodPressureCategory(systolic.value, diastolic.value)
        }
      })
  }

  protected getBloodPressureCategory (systolic, diastolic): string {
    if (systolic > 180 || diastolic > 110) {
      return 'HYPERTENSIVE_CASE'
    }
    if (systolic >= 160 || diastolic >= 100) {
      return 'HIGH2'
    }
    if (systolic > 140 || diastolic > 90) {
      return 'HIGH1'
    }
    if (systolic > 130 || diastolic > 85) {
      return 'PREHYPERTENSION'
    }
    if (systolic > 120 || diastolic > 80) {
      return 'NORMAL'
    }
    return 'OPTIMAL'
  }

  private getXAxisLabelStyle () {
    let style = CHART_LABEL_TWO_ROWS_FONT_STYLE
    if (window.innerWidth < MIN_LANDSCAPE_WIDTH) {
      style = {
        ...style,
        fontSize: '0.813rem'
      }
    }

    return style
  }

  private getYAxisLabelStyle () {
    let style = { color: '#212121', fontSize: '17.39px' }
    if (window.innerWidth < MIN_LANDSCAPE_WIDTH) {
      style = {
        ...style,
        fontSize: '0.813rem'
      }
    }

    return style
  }

  private updateComponentOnOrientationChange () {
    this.$forceUpdate()
  }

  private addEventListeners () {
    this.portraitMatch.addEventListener('change', this.updateComponentOnOrientationChange)
    window.addEventListener('beforeprint', this.beforePrint)
    window.addEventListener('afterprint', this.afterPrint)
  }

  private removeEventListeners () {
    this.portraitMatch.removeEventListener('change', this.updateComponentOnOrientationChange)
    window.removeEventListener('beforeprint', this.beforePrint)
    window.removeEventListener('afterprint', this.afterPrint)
  }

}
